/**
 * Live Recording — client render model (LIVE-OR-STREAMING).
 *
 * Pure state the display renders from: accumulates the frames the aggregator
 * streams (one per server tick) into rolling per-metric, per-participant value
 * series plus a bounded marker log. Framework-free so it's unit-tested on its
 * own; the React component just feeds it frames and reads it back on repaint.
 */

/** One divergence marker, as the server serialized it (frame_to_dict). */
export interface WallMarker {
  metric: string;
  t: number;
  hi: string;
  hi_z: number;
  lo: string;
  lo_z: number;
  split: number;
}

/** One aggregator tick. */
export interface WallFrame {
  t: number;
  /** metric -> { participant_id: value } */
  values: Record<string, Record<string, number>>;
  markers: WallMarker[];
}

/** A marker plus the frame index it landed on, so the wall can place it on the
 *  scrolling x-axis and expire it as it slides off. */
export interface PlacedMarker {
  marker: WallMarker;
  frame: number;
}

export class LiveWallBuffer {
  /** metric -> participant -> rolling values (newest last). */
  private hist: Record<string, Record<string, number[]>> = {};
  private participants = new Set<string>();
  private placed: PlacedMarker[] = [];
  private frames = 0;

  constructor(
    private readonly window = 240,
    private readonly maxMarkers = 40,
  ) {}

  /** Fold one streamed frame in. */
  ingest(frame: WallFrame): void {
    this.frames += 1;
    for (const [metric, byPid] of Object.entries(frame.values ?? {})) {
      const perMetric = (this.hist[metric] ??= {});
      for (const [pid, value] of Object.entries(byPid)) {
        this.participants.add(pid);
        const arr = (perMetric[pid] ??= []);
        arr.push(value);
        if (arr.length > this.window) arr.shift();
      }
    }
    for (const marker of frame.markers ?? []) {
      this.placed.push({ marker, frame: this.frames });
      if (this.placed.length > this.maxMarkers) this.placed.shift();
    }
    // Drop markers that have scrolled off the visible window.
    while (this.placed.length && this.placed[0].frame < this.frames - this.window) {
      this.placed.shift();
    }
  }

  /** The metrics seen so far, in first-seen order. */
  metrics(): string[] {
    return Object.keys(this.hist);
  }

  /** Sorted participant ids (stable colour assignment). */
  participantIds(): string[] {
    return [...this.participants].sort();
  }

  /** Rolling series for one metric: participant -> values. */
  series(metric: string): Record<string, number[]> {
    return this.hist[metric] ?? {};
  }

  /** Placed markers for one metric, for on-canvas rendering. */
  markersFor(metric: string): PlacedMarker[] {
    return this.placed.filter((p) => p.marker.metric === metric);
  }

  /** Newest-first marker log across all metrics, for the text panel. */
  markerLog(limit = 12): WallMarker[] {
    return this.placed
      .slice()
      .reverse()
      .map((p) => p.marker)
      .slice(0, limit);
  }

  /** How many frames have been ingested (the scrolling x cursor). */
  frameCount(): number {
    return this.frames;
  }

  /** X position (0..1) of a marker in the current window, or null if off it. */
  markerX(m: PlacedMarker): number | null {
    const base = this.frames - this.window;
    const x = (m.frame - base) / this.window;
    return x >= 0 && x <= 1 ? x : null;
  }
}
